import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ActionWithStatus, CreateActionInput } from '@databro/shared';
import {
  Button,
  Input,
  Label,
  Textarea,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@databro/ui';

interface FormData {
  slug: string;
  name: string;
  platform: 'shopee' | 'tiktok' | 'custom';
  enabled: boolean;
  pageUrl: string;
  scheduleType: 'manual' | 'periodic';
  intervalMs?: number;
  webhookUrl?: string;
  webhookUrls?: string[];
  executionSettings: {
    serverEnabled: boolean;
    workerEnabled: boolean;
    browserEnabled: boolean;
    activeTab?: boolean;
  };
  browserExecution?: {
    pageUrl: string;
    responseUrlPattern: string;
    timeout?: number;
    activeTab?: boolean;
  } | null;
}

const DEFAULT_FORM_DATA: FormData = {
  slug: '',
  name: '',
  platform: 'shopee',
  enabled: true,
  pageUrl: '',
  scheduleType: 'manual',
  executionSettings: {
    serverEnabled: true,
    workerEnabled: true,
    browserEnabled: true,
  },
  browserExecution: null,
};

const DEFAULT_CAPTURE_PATTERNS = [
  {
    urlPattern: '*api*',
    method: '*',
    headers: ['Cookie', 'Authorization'],
  },
];

const DEFAULT_AUTH_CHECK = { type: 'status' as const, codes: [401, 403] };
const DEFAULT_SUCCESS_CRITERIA = { type: 'status' as const, codes: [200] };

const AI_EXAMPLE_MARKDOWN = `# Action JSON Schema

## Example
\`\`\`json
{
  "slug": "shopee-daily-ads-report",
  "name": "Shopee Daily Ads Report",
  "platform": "shopee",
  "enabled": true,
  "pageUrl": "https://seller.shopee.co.id/portal/marketing/ads",
  "capturePatterns": [
    {
      "urlPattern": "*api*",
      "method": "*",
      "headers": ["Cookie", "Authorization"]
    }
  ],
  "authCheck": {
    "type": "status",
    "codes": [401, 403]
  },
  "scheduleType": "periodic",
  "intervalMs": 1800000,
  "requests": [
    {
      "name": "Get Ads Performance",
      "url": "https://seller.shopee.co.id/api/v3/ads/performance",
      "method": "GET",
      "headers": {},
      "queryParams": {
        "start_time": "{{day_start}}",
        "end_time": "{{day_end}}"
      }
    }
  ],
  "successCriteria": {
    "type": "status",
    "codes": [200]
  },
  "executionSettings": {
    "serverEnabled": true,
    "workerEnabled": true,
    "browserEnabled": true
  },
  "webhookUrl": "https://your-server.com/webhook"
}
\`\`\`

## Field Definitions

| Field | Type | Required | Description |
|-------|------|----------|-------------|
| \`slug\` | string | Yes | URL-friendly identifier (lowercase, hyphens) |
| \`name\` | string | Yes | Display name for the action |
| \`platform\` | "shopee" \\| "tiktok" \\| "custom" | Yes | Target platform |
| \`enabled\` | boolean | Yes | Whether action is active |
| \`pageUrl\` | string | Yes | URL where API requests are captured from |
| \`capturePatterns\` | array | Yes | Patterns to match when capturing auth headers |
| \`scheduleType\` | "manual" \\| "periodic" | Yes | Execution schedule type |
| \`intervalMs\` | number | No | Interval in milliseconds (required if periodic) |
| \`requests\` | array | Yes | Array of API requests to execute |
| \`webhookUrl\` | string | No | URL to POST results to after execution |

## Template Variables

Use these placeholders in \`queryParams\` or \`body\`:

| Variable | Description |
|----------|-------------|
| \`{{day_start}}\` | Start of current day (Unix timestamp) |
| \`{{day_end}}\` | End of current day (Unix timestamp) |
| \`{{spc_cds}}\` | Shopee SPC cookie value (auto-extracted) |
`;

interface ActionFormProps {
  action: ActionWithStatus | null;
  clients: Array<{ id: string; name?: string }>;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ActionForm({
  action,
  clients,
  onClose,
  onSuccess,
}: ActionFormProps) {
  const [viewMode, setViewMode] = useState<'form' | 'json'>('form');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopyForAI = async () => {
    try {
      await navigator.clipboard.writeText(AI_EXAMPLE_MARKDOWN);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Failed to copy to clipboard');
    }
  };

  // Combine webhookUrl and webhookUrls into a unified list for editing
  const getInitialWebhookUrls = (): string[] => {
    if (!action) return [''];
    const urls: string[] = [];
    if (action.webhookUrl) urls.push(action.webhookUrl);
    if (action.webhookUrls) urls.push(...action.webhookUrls);
    // Deduplicate
    const unique = [...new Set(urls)];
    return unique.length > 0 ? unique : [''];
  };

  const [webhookUrlsList, setWebhookUrlsList] = useState<string[]>(getInitialWebhookUrls);

  const [formData, setFormData] = useState<FormData>(() =>
    action
      ? {
          slug: action.slug,
          name: action.name,
          platform: action.platform,
          enabled: action.enabled,
          pageUrl: action.pageUrl,
          scheduleType: action.scheduleType,
          intervalMs: action.intervalMs,
          webhookUrl: action.webhookUrl,
          webhookUrls: action.webhookUrls,
          executionSettings: (action.executionSettings as FormData['executionSettings']) || DEFAULT_FORM_DATA.executionSettings,
          browserExecution: (action.browserExecution as FormData['browserExecution']) || null,
        }
      : DEFAULT_FORM_DATA
  );

  const [intervalMinutes, setIntervalMinutes] = useState(
    action?.scheduleType === 'periodic' ? (action.intervalMs || 0) / 60000 : 30
  );

  const [capturePatternsJson, setCapturePatternsJson] = useState(
    JSON.stringify(action?.capturePatterns || DEFAULT_CAPTURE_PATTERNS, null, 2)
  );
  const [requestsJson, setRequestsJson] = useState(
    JSON.stringify(action?.requests || [], null, 2)
  );

  const [fullJson, setFullJson] = useState(() => {
    if (action) {
      return JSON.stringify(
        {
          slug: action.slug,
          name: action.name,
          platform: action.platform,
          enabled: action.enabled,
          pageUrl: action.pageUrl,
          capturePatterns: action.capturePatterns,
          authCheck: action.authCheck,
          scheduleType: action.scheduleType,
          intervalMs: action.intervalMs,
          requests: action.requests,
          successCriteria: action.successCriteria,
          executionSettings: action.executionSettings,
          browserExecution: action.browserExecution || undefined,
          webhookUrl: action.webhookUrl,
        },
        null,
        2
      );
    }
    return JSON.stringify(
      {
        slug: '',
        name: '',
        platform: 'shopee',
        enabled: true,
        pageUrl: '',
        capturePatterns: DEFAULT_CAPTURE_PATTERNS,
        authCheck: DEFAULT_AUTH_CHECK,
        scheduleType: 'manual',
        requests: [],
        successCriteria: DEFAULT_SUCCESS_CRITERIA,
        executionSettings: DEFAULT_FORM_DATA.executionSettings,
      },
      null,
      2
    );
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateActionInput) => api.createAction(data),
    onSuccess,
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateActionInput }) =>
      api.updateAction(id, data),
    onSuccess,
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to update'),
  });

  const switchToJsonMode = () => {
    let capturePatterns, requests;
    try {
      capturePatterns = JSON.parse(capturePatternsJson);
    } catch {
      capturePatterns = DEFAULT_CAPTURE_PATTERNS;
    }
    try {
      requests = JSON.parse(requestsJson);
    } catch {
      requests = [];
    }

    // Get valid webhook URLs from the list
    const validWebhookUrls = webhookUrlsList.filter((url) => url.trim() !== '');

    const data: CreateActionInput = {
      slug: formData.slug,
      name: formData.name,
      platform: formData.platform,
      enabled: formData.enabled,
      pageUrl: formData.pageUrl,
      capturePatterns,
      authCheck: DEFAULT_AUTH_CHECK,
      scheduleType: formData.scheduleType,
      intervalMs: formData.scheduleType === 'periodic' ? intervalMinutes * 60000 : undefined,
      requests,
      successCriteria: DEFAULT_SUCCESS_CRITERIA,
      executionSettings: formData.executionSettings,
      webhookUrl: validWebhookUrls[0],
      webhookUrls: validWebhookUrls.length > 0 ? validWebhookUrls : undefined,
    };

    setFullJson(JSON.stringify(data, null, 2));
    setViewMode('json');
  };

  const switchToFormMode = () => {
    try {
      const parsed = JSON.parse(fullJson);
      setFormData({
        slug: parsed.slug || '',
        name: parsed.name || '',
        platform: parsed.platform || 'shopee',
        enabled: parsed.enabled ?? true,
        pageUrl: parsed.pageUrl || '',
        scheduleType: parsed.scheduleType || 'manual',
        intervalMs: parsed.intervalMs,
        webhookUrl: parsed.webhookUrl,
        webhookUrls: parsed.webhookUrls,
        executionSettings: parsed.executionSettings || DEFAULT_FORM_DATA.executionSettings,
      });
      if (parsed.scheduleType === 'periodic' && parsed.intervalMs) {
        setIntervalMinutes(parsed.intervalMs / 60000);
      }
      // Update webhook URLs list from parsed JSON
      const urls: string[] = [];
      if (parsed.webhookUrls && parsed.webhookUrls.length > 0) {
        urls.push(...parsed.webhookUrls);
      } else if (parsed.webhookUrl) {
        urls.push(parsed.webhookUrl);
      }
      setWebhookUrlsList(urls.length > 0 ? [...new Set(urls)] : ['']);
      setCapturePatternsJson(JSON.stringify(parsed.capturePatterns || DEFAULT_CAPTURE_PATTERNS, null, 2));
      setRequestsJson(JSON.stringify(parsed.requests || [], null, 2));
    } catch {
      // Keep current state if JSON is invalid
    }
    setViewMode('form');
  };

  // Helper functions for managing webhook URLs
  const addWebhookUrl = () => {
    setWebhookUrlsList([...webhookUrlsList, '']);
  };

  const removeWebhookUrl = (index: number) => {
    if (webhookUrlsList.length > 1) {
      setWebhookUrlsList(webhookUrlsList.filter((_, i) => i !== index));
    }
  };

  const updateWebhookUrl = (index: number, value: string) => {
    const updated = [...webhookUrlsList];
    updated[index] = value;
    setWebhookUrlsList(updated);
  };

  // Get valid webhook URLs (filter empty strings)
  const getValidWebhookUrls = () => {
    return webhookUrlsList.filter((url) => url.trim() !== '');
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    let capturePatterns, requests;
    try {
      capturePatterns = JSON.parse(capturePatternsJson);
    } catch {
      setError('Invalid capture patterns JSON');
      return;
    }
    try {
      requests = JSON.parse(requestsJson);
    } catch {
      setError('Invalid requests JSON');
      return;
    }

    // Get valid webhook URLs
    const validWebhookUrls = getValidWebhookUrls();

    const data: CreateActionInput = {
      slug: formData.slug,
      name: formData.name,
      platform: formData.platform,
      enabled: formData.enabled,
      pageUrl: formData.pageUrl,
      capturePatterns,
      authCheck: DEFAULT_AUTH_CHECK,
      scheduleType: formData.scheduleType,
      intervalMs: formData.scheduleType === 'periodic' ? intervalMinutes * 60000 : undefined,
      requests,
      successCriteria: DEFAULT_SUCCESS_CRITERIA,
      executionSettings: formData.executionSettings,
      browserExecution: formData.browserExecution || undefined,
      // For backward compatibility, set webhookUrl to first URL if any
      webhookUrl: validWebhookUrls[0] || undefined,
      // Set webhookUrls to remaining URLs (or all URLs if using new format)
      webhookUrls: validWebhookUrls.length > 0 ? validWebhookUrls : undefined,
    };

    if (action) {
      updateMutation.mutate({ id: action.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleJsonSubmit = () => {
    setError('');
    try {
      const data = JSON.parse(fullJson) as CreateActionInput;
      if (action) {
        updateMutation.mutate({ id: action.id, data });
      } else {
        createMutation.mutate(data);
      }
    } catch {
      setError('Invalid JSON');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{action ? 'Edit Action' : 'Create Action'}</DialogTitle>
        </DialogHeader>

        <Tabs value={viewMode} onValueChange={(v: string) => {
          if (v === 'json' && viewMode === 'form') switchToJsonMode();
          if (v === 'form' && viewMode === 'json') switchToFormMode();
        }} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="form">Form</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto mt-4">
            <TabsContent value="json" className="mt-0">
              <div className="space-y-2">
                <Textarea
                  value={fullJson}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFullJson(e.target.value)}
                  className="h-96 font-mono text-sm"
                  placeholder="Enter action JSON..."
                />
                <p className="text-sm text-muted-foreground">
                  Paste complete action JSON. See examples/ folder for templates.
                </p>
              </div>
            </TabsContent>

            <TabsContent value="form" className="mt-0">
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug</Label>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                      placeholder="e.g., shopee-daily-ads"
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      URL-friendly identifier (lowercase, hyphens only)
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Shopee Daily Ads"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="platform">Platform</Label>
                    <Select
                      value={formData.platform}
                      onValueChange={(v: string) => setFormData({ ...formData, platform: v as FormData['platform'] })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shopee">Shopee</SelectItem>
                        <SelectItem value="tiktok">TikTok Shop</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pageUrl">Page URL</Label>
                  <Input
                    id="pageUrl"
                    type="url"
                    value={formData.pageUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, pageUrl: e.target.value })}
                    placeholder="https://seller.shopee.co.id/portal/product/list"
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    URL of the page where API requests happen (for capturing)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Schedule</Label>
                    <div className="flex items-center gap-2">
                      <Select
                        value={formData.scheduleType}
                        onValueChange={(v: string) => setFormData({ ...formData, scheduleType: v as 'manual' | 'periodic' })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">Manual</SelectItem>
                          <SelectItem value="periodic">Periodic</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.scheduleType === 'periodic' && (
                        <>
                          <span className="text-sm text-muted-foreground">every</span>
                          <Input
                            type="number"
                            value={intervalMinutes}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIntervalMinutes(Number(e.target.value))}
                            className="w-20"
                            min={1}
                          />
                          <span className="text-sm text-muted-foreground">minutes</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Webhook URLs (optional)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addWebhookUrl}
                    >
                      + Add URL
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {webhookUrlsList.map((url, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          type="url"
                          value={url}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateWebhookUrl(index, e.target.value)}
                          placeholder="https://your-server.com/webhook"
                          className="flex-1"
                        />
                        {webhookUrlsList.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeWebhookUrl(index)}
                            className="px-2 text-destructive hover:text-destructive"
                          >
                            ✕
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Add multiple webhook URLs to receive execution results at different endpoints
                  </p>
                </div>

                <div className="space-y-3">
                  <Label>Execution Tiers</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose which execution methods to try (in order of priority)
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="tier1"
                        checked={formData.executionSettings.serverEnabled}
                        onCheckedChange={(checked: boolean | 'indeterminate') =>
                          setFormData({
                            ...formData,
                            executionSettings: { ...formData.executionSettings, serverEnabled: checked === true },
                          })
                        }
                      />
                      <Label htmlFor="tier1" className="text-sm font-normal">
                        Tier 1: Server (fastest, may not work for all APIs)
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="tier2"
                        checked={formData.executionSettings.workerEnabled}
                        onCheckedChange={(checked: boolean | 'indeterminate') =>
                          setFormData({
                            ...formData,
                            executionSettings: { ...formData.executionSettings, workerEnabled: checked === true },
                          })
                        }
                      />
                      <Label htmlFor="tier2" className="text-sm font-normal">
                        Tier 2: Extension Worker (uses browser context)
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="tier3"
                        checked={formData.executionSettings.browserEnabled}
                        disabled
                      />
                      <Label htmlFor="tier3" className="text-sm font-normal text-muted-foreground">
                        Tier 3: Browser Tab (always available as fallback)
                      </Label>
                    </div>
                    {formData.executionSettings.browserEnabled && (
                      <div className="flex items-center gap-2 pl-6">
                        <Checkbox
                          id="activeTabCapture"
                          checked={formData.executionSettings.activeTab ?? false}
                          onCheckedChange={(checked: boolean | 'indeterminate') =>
                            setFormData({
                              ...formData,
                              executionSettings: { ...formData.executionSettings, activeTab: checked === true },
                            })
                          }
                        />
                        <Label htmlFor="activeTabCapture" className="text-sm font-normal">
                          Open tab as active (some sites defer API calls until tab is visible)
                        </Label>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Browser Execution Config (Tier 3)</Label>
                    <Checkbox
                      id="browserExecEnabled"
                      checked={!!formData.browserExecution}
                      onCheckedChange={(checked: boolean | 'indeterminate') =>
                        setFormData({
                          ...formData,
                          browserExecution: checked === true
                            ? { pageUrl: formData.pageUrl || '', responseUrlPattern: '', timeout: 30000, activeTab: false }
                            : null,
                        })
                      }
                    />
                  </div>
                  {formData.browserExecution && (
                    <div className="space-y-2 pl-2 border-l-2 border-muted">
                      <div className="space-y-1">
                        <Label className="text-sm">Response URL Pattern</Label>
                        <Input
                          value={formData.browserExecution.responseUrlPattern}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setFormData({
                              ...formData,
                              browserExecution: { ...formData.browserExecution!, responseUrlPattern: e.target.value },
                            })
                          }
                          placeholder="*api/v4/shop/rcmd_items*"
                          className="font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">URL pattern to intercept (* as wildcard)</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Page URL (override)</Label>
                        <Input
                          value={formData.browserExecution.pageUrl}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setFormData({
                              ...formData,
                              browserExecution: { ...formData.browserExecution!, pageUrl: e.target.value },
                            })
                          }
                          placeholder="Leave empty to use action page URL"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Timeout (ms)</Label>
                        <Input
                          type="number"
                          value={formData.browserExecution.timeout || 30000}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setFormData({
                              ...formData,
                              browserExecution: { ...formData.browserExecution!, timeout: parseInt(e.target.value) || 30000 },
                            })
                          }
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="activeTab"
                          checked={formData.browserExecution.activeTab ?? false}
                          onCheckedChange={(checked: boolean | 'indeterminate') =>
                            setFormData({
                              ...formData,
                              browserExecution: { ...formData.browserExecution!, activeTab: checked === true },
                            })
                          }
                        />
                        <Label htmlFor="activeTab" className="text-sm font-normal">
                          Open tab as active (some sites defer API calls until tab is visible)
                        </Label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Capture Patterns (JSON)</Label>
                  <Textarea
                    value={capturePatternsJson}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCapturePatternsJson(e.target.value)}
                    className="h-24 font-mono text-sm"
                  />
                  <p className="text-sm text-muted-foreground">
                    Patterns to match when capturing auth headers
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Requests (JSON)</Label>
                  <Textarea
                    value={requestsJson}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRequestsJson(e.target.value)}
                    className="h-48 font-mono text-sm"
                  />
                  <p className="text-sm text-muted-foreground">
                    Array of requests to execute. Use {'{{day_start}}'}, {'{{day_end}}'}, {'{{spc_cds}}'} for dynamic values.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="enabled"
                    checked={formData.enabled}
                    onCheckedChange={(checked: boolean | 'indeterminate') =>
                      setFormData({ ...formData, enabled: checked === true })
                    }
                  />
                  <Label htmlFor="enabled" className="text-sm font-medium">
                    Enabled
                  </Label>
                </div>
              </form>
            </TabsContent>
          </div>
        </Tabs>

        {error && (
          <div className="p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            {error}
          </div>
        )}

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={handleCopyForAI}
            className="mr-auto"
          >
            {copied ? 'Copied!' : 'Copy for AI'}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
            onClick={viewMode === 'json' ? handleJsonSubmit : (e) => {
              e.preventDefault();
              const form = document.querySelector('form');
              if (form) form.requestSubmit();
            }}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {action ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
